import { z } from "zod";
/**
 * Tenant config-as-code (decided 2026-07-27 — theming.md). One entry per tenant, in git, reviewed — NOT in the DB.
 * Browser-safe on purpose: `apps/web` imports this for pre-auth branding, so it must stay dependency-light (zod only)
 * and must never pull in ORM/server code.
 */
/** Deployment environments. Host AND issuer differ per env, and neither is derivable by string rules. */
export const EnvName = z.enum(["local", "staging", "production"]);
/**
 * An alias route that must redirect to the tenant's canonical host.
 *
 * `pathPrefix` exists because the legacy sub-tenants were **path**-based (`xrarea.arcreator.at/lumpi`) and their
 * **printed QR codes** — on physical equipment, not reprintable — encode `?arc_facility=` against that host+path
 * (hosting-dns.md). A host-only model cannot resolve them: one shared host maps to three different tenants.
 */
export const AliasRoute = z.object({
    host: z.string().min(1),
    /**
     * Must start with "/" and match on a SEGMENT boundary (`/lumpi` matches `/lumpi/x`, never `/lumpifoo`).
     * A TRAILING SLASH is rejected: `/lumpi/` would make the runtime test `startsWith("/lumpi//")`, which no real
     * request matches — the sub-tenant would then silently fall through to the bare-host alias and land on the WRONG
     * tenant. Empty segments (`//`) are rejected for the same reason.
     */
    pathPrefix: z
        .string()
        .regex(/^\/[^/]+(?:\/[^/]+)*$/, "pathPrefix must be '/segment' with no trailing slash or empty segments")
        .optional(),
});
export const TenantEnvironment = z.object({
    /** The ONE origin the tenant is served on. Everything else 301s here (auth-tenancy.md: one canonical origin). */
    canonicalHost: z.string().min(1),
    /**
     * Exact token `iss`, byte-for-byte. Note staging/production Keycloak lives under a **`/auth`** relative path, so
     * this is NOT `<origin>/realms/<realm>` — never reconstruct it from parts (hosting-dns.md).
     */
    issuer: z.string().url(),
    aliases: z.array(AliasRoute).default([]),
    /**
     * Keycloak IdP alias to jump straight to (`kc_idp_hint`). **Per-environment on purpose**: production Takeda
     * federates to EntraID, but the LOCAL realm has federation disabled so a developer can sign in with a local
     * user. A hint naming a disabled/absent provider just breaks login.
     * From config, NEVER a URL parameter.
     */
    idpHint: z.string().optional(),
    /**
     * The legacy `pageURL`s a printed deep-link label may carry (ADR-029): three realms printed host PLUS a path
     * (`https://xrarea.arcreator.at/lumpi`). A scanner must ACCEPT these prefixes on old labels; new labels print this
     * app's own origin. Recorded here so the acceptance list is config, not code.
     */
    qrLegacyDeepLinkBases: z.array(z.string().url()).default([]),
});
/**
 * The UI languages the shell ships — **English and German only** (owner decision).
 *
 * Declared HERE rather than in `apps/web` so there is exactly ONE list: the web shell derives its `LOCALES` from
 * this enum. A second declaration could drift, and the failure would be silent and per-tenant — adding `fr` to the
 * UI list while the schema still rejects `defaultLocale: "fr"` breaks precisely one tenant, at import time.
 *
 * Scope is the SHELL's own strings and `<html lang>`. Translating manual *content* is a different problem (the
 * legacy `TextLoader` + the §-glossary + dual-format multilingual values) and belongs to its own ADR.
 */
export const TenantLocale = z.enum(["en", "de"]);
const Hex = z.string().regex(/^#[0-9a-f]{6}$/i);
/**
 * The legacy 12-slot `realm.colors` palette, stored **verbatim and positionally 1:1 with the DB column** so the ETL
 * and any legacy cross-check are trivial. (Verified in production: slots 5–12 are byte-identical across all 5 realms —
 * effectively a shared neutral scale — while slots 1–4 are the per-tenant brand ramp. Only two distinct ramps exist.)
 *
 * These are RAW source values, not design tokens. Semantic tokens are **derived** from them in the UI's token layer,
 * because several raw slots fail WCAG AA and must never be used directly for text (design-system-brief.md):
 * `#ef7d00` is 2.76:1 (decorative only) and slots 5–8/10–11 are borders/backgrounds, not text colours.
 */
export const Palette12 = z.tuple([Hex, Hex, Hex, Hex, Hex, Hex, Hex, Hex, Hex, Hex, Hex, Hex]);
export const BrandTokens = z.object({
    /** `realm.colors` verbatim. `palette[0]` is the brand/display colour (legacy `primaryColor`). */
    palette: Palette12,
    /**
     * AA-safe partner for `palette[0]`, used wherever contrast is a requirement: body text, small icons, and
     * white-on-brand buttons (≥ 4.5:1 on white). Equal to `palette[0]` when that already passes — the XR ramp's
     * `#124468` is 10.23:1 — but Takeda's red is only ~4.45–4.47:1 and therefore needs a distinct value.
     * WCAG AA is an acceptance criterion (ENGINEERING-GUARDRAILS), so this is required, not cosmetic.
     */
    primaryInteractive: Hex,
    /**
     * The same guarantee on the DARK surface (`--surface: #2e3c47`). The light partner is validated only against
     * WHITE, and re-using it on dark is catastrophic: the XR ramp's #124468 scores **1.11:1** there — effectively
     * invisible — and Takeda's red 2.50:1. This also backs `--focus-ring`, where WCAG SC 1.4.11 requires 3:1, so a
     * missing dark pair breaks *keyboard* accessibility, not just aesthetics.
     */
    primaryInteractiveDark: Hex,
    /**
     * OPTIONAL — the SOLID `--primary` button/chip/banner **fill** on the light surface, for a tenant whose main colour is
     * not its brand colour (Takeda: slate, with red as the content colour). Defaults to {@link primaryInteractive}, which
     * keeps backing brand text and icons either way. The ink is derived (`readableInk`).
     */
    primaryFill: Hex.optional(),
    /**
     * OPTIONAL — the same fill on the dark surface. Defaults to {@link primaryInteractiveDark} (the lightened accent, which
     * then carries dark ink). Blue tenants' `#124468` is 1.11:1 on the dark surface, so a saturated fill would be invisible
     * there. The ink is derived (`readableInk`), so AA text contrast (SC 1.4.3) holds either way.
     */
    primaryFillDark: Hex.optional(),
    /**
     * OPTIONAL — replaces the shared brand orange (`--orange`: content buttons, selection marks) in both themes. Unset ⇒
     * the orange, with its owner-approved white label. The label on a replacement is derived (`readableInk`).
     */
    accentFill: Hex.optional(),
});
/**
 * Per-tenant feature flags, extracted from the legacy `realms/<realm>/<realm>_variables.js` globals.
 *
 * **Every flag here has a consumer.** Four once did not — `adminHasAdditionalRights`,
 * `hololensAppDownloadAvailable`, `masterFolderEnabled` and `reportingOnlyForAdmin` were declared, set per tenant,
 * and read by nothing (ADR-047 D4). They were removed rather than left: a required `false` is a CLAIM that a tenant
 * has something switched off, and config that asserts a policy nothing enforces is the same failure class as a
 * guard no call site invokes — it reads as enforced. Their per-realm legacy values are preserved in PARITY-GAPS so
 * the information survives for whoever builds those features.
 *
 * The rule this leaves behind: **add a flag when its consumer exists, not before** — and prefer a mechanism where
 * one already decides the question (the kiosk needs no flag because a realm without the role has no kiosk;
 * sensors became the SuperAdmin gate, ADR-040 D2).
 *
 * `Integrations` below is deliberately NOT treated the same way: its fields are `.optional()`, so an unset one
 * asserts nothing about any tenant.
 */
export const FeatureFlags = z.object({
    /**
     * Ratings (ADR-021 / ADR-034 D6): the legacy `realm.ratingsEnabled` — gates the Dashboard's Ratings page and the
     * rating filter. Default ON (the rewrite treated ratings as always-on so far; the owner took that default 2026-09-11)
     * — a tenant switches it off explicitly here.
     */
    ratingsEnabled: z.boolean().default(true),
    /**
     * Facility-navigation UI variant (FN / ADR-014). `"classic"` = the master-detail tree + manuals-in-the-same-view;
     * `"drilldown"` = the legacy-style root list → one-level drill-down (breadcrumb) with manuals in a separate view.
     * Defaults to `"classic"` so a tenant that predates the flag (or a new client) keeps the current layout unless it
     * explicitly opts in. Existing tenants takeda + xr-area are flipped to `"drilldown"` in FN2 (not here).
     */
    facilityNav: z.enum(["classic", "drilldown"]).default("classic"),
    /**
     * QR labels (ADR-029). `qrCodeWithLink`: a single-facility code is printed as a DEEP LINK (`<origin>?arc_facility=<id>`)
     * rather than the `facilityIds[…]` payload — the legacy GLOBAL default is `true` (`ARLMS_generalVariables.js:41`)
     * and no realm turns it off. `qrCodeLabelTitle`: the printed label's heading; when unset the label reads the
     * LITERAL "XR Creator", exactly as the legacy (`ARLMS_PrintOut.js:52-58`) — not the display name, which would
     * silently rebrand reprinted labels. Only takeda ever set it.
     */
    qrCodeWithLink: z.boolean().default(true),
    /** The read-only "Nutzbar in" block in the manual details (ADR-057, legacy `sidebarPlatformsVisible`). Takeda has it off. */
    sidebarPlatformsVisible: z.boolean().default(true),
    qrCodeLabelTitle: z.string().min(1).optional(),
});
const PmKey = z.string().regex(/^[a-z0-9-]{1,40}$/);
/** The tag colours. Stays in step with `PM_TAG_COLORS` in `@arc/contracts` (a test in `apps/api` holds them together). */
export const PmTagColor = z.enum(["blue", "green", "orange", "purple", "teal", "red", "yellow", "gray"]);
/**
 * The project-management board at `/pm` (ADR-055, generic since 2026-10-31): every realm has it, unused is fine. These
 * are the realm's SEED values: the API inserts `columns` in the realm's first PM transaction, array order = `sort_order`,
 * and from then on admins edit the columns in the UI, so a later change here does not touch a seeded realm.
 */
export const PmBoardConfig = z
    .object({
    columns: z
        .array(z.object({ key: PmKey, title: z.string().min(1), role: z.enum(["initial", "done"]).optional(), persistent: z.boolean().default(false) }))
        .default([
        { key: "offen", title: "Offen", role: "initial", persistent: false },
        { key: "create", title: "Create", persistent: false },
        { key: "erledigt", title: "Erledigt", role: "done", persistent: false },
    ]),
    /** The labels are the realm's own names, so they live here and not in the locale files. */
    institutes: z.array(z.object({ key: PmKey, label: z.string().min(1) })).default([{ key: "create", label: "create" }]),
    /** Who is responsible for a task. Seeded together with the columns; from then on the admin routes own them. */
    tags: z.array(z.object({ key: PmKey, label: z.string().min(1), color: PmTagColor })).default([{ key: "create", label: "create", color: "blue" }]),
    /** `true`: any signed-in user of the realm may use `/pm`. `false`: only SuperAdmins and assigned members. */
    openToAllUsers: z.boolean().default(false),
})
    .superRefine((c, ctx) => {
    const once = (role) => c.columns.filter((x) => x.role === role).length === 1;
    if (!once("initial") || !once("done"))
        ctx.addIssue({ code: "custom", message: "exactly one initial and one done column" });
    if (new Set(c.columns.map((x) => x.key)).size !== c.columns.length)
        ctx.addIssue({ code: "custom", message: "column keys must be unique" });
    if (new Set(c.institutes.map((x) => x.key)).size !== c.institutes.length)
        ctx.addIssue({ code: "custom", message: "institute keys must be unique" });
    if (new Set(c.tags.map((x) => x.key)).size !== c.tags.length)
        ctx.addIssue({ code: "custom", message: "tag keys must be unique" });
    if (new Set(c.tags.map((x) => x.label.toLowerCase())).size !== c.tags.length)
        ctx.addIssue({ code: "custom", message: "tag labels must be unique" });
});
export const Integrations = z.object({
    /** Video-call SDK app id. NOT a secret in the OIDC sense, but still injected, never committed. */
    agoraAppId: z.string().optional(),
    matomo: z.object({ url: z.string().url(), siteId: z.string() }).optional(),
});
export const TenantConfig = z.object({
    /** Keycloak realm name — the STABLE key across environments. Never derived from a hostname. */
    realm: z.string().min(1),
    /** The preserved legacy int `realm.id` — the value used as the Postgres RLS tenant GUC. Must match the migration. */
    realmId: z.number().int().positive(),
    /** White-label product name — the app is renamed per tenant (e.g. Takeda = "Knowledge Database"). */
    productName: z.string().min(1),
    /**
     * The CLIENT / organization name, e.g. "Takeda" or "ABZ Braunau".
     * Deliberately distinct from `productName`: they differ for most tenants (takeda's product is
     * "Knowledge Database", abzbraunau's is "XR Creator"), so reusing one for the other shows the wrong name in
     * the footer. Owner-confirmed values — do not invent a customer's own name.
     */
    clientName: z.string().min(1),
    /** The single public SPA client. There is no web/api client split. */
    clientId: z.string().min(1).default("arcreator"),
    /**
     * The tenant's own language — the fallback when the browser asks for one the shell does not ship (ADR-047 D2).
     *
     * **The LAST step of the chain, not an override.** An explicit browser preference still wins: `stored choice →
     * browser → this → "en"`. Putting the tenant ahead of the browser would force German onto someone whose browser
     * says English, and — because this field is required — would make the browser step unreachable entirely.
     *
     * **Required, with no schema default.** An unchosen `"en"` is exactly the defect this fixes: the legacy set
     * `defaultLanguage = "de"` globally (`ARLMS_generalVariables.js:14`, no realm overriding it) and four of the five
     * tenants are Austrian, so a silent English default is wrong for almost everyone. Requiring it makes a new tenant
     * decide at parse time — the completeness guarantee enforced by the type rather than by a test.
     */
    defaultLocale: TenantLocale,
    brand: BrandTokens,
    featureFlags: FeatureFlags,
    pmBoard: PmBoardConfig.default({}),
    integrations: Integrations.default({}),
    /** `staging` may be absent until its hosts are confirmed. */
    environments: z.object({
        local: TenantEnvironment,
        staging: TenantEnvironment.optional(),
        production: TenantEnvironment,
    }),
});
//# sourceMappingURL=schema.js.map